import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, X, Loader2, AlertCircle } from 'lucide-react';
import { api, parseApiError } from '../lib/api';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { displayClanName } from '../lib/displayName';

const TOKEN_KEY = 'ggm_portal_token';

type ClaimResponse = Awaited<ReturnType<typeof api.redeemClaimToken>>;

export function ClaimPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [state, setState] = useState<
    | { kind: 'redeeming' }
    | { kind: 'error'; message: string }
    | { kind: 'ready'; res: ClaimResponse }
    | { kind: 'acting' }
    | { kind: 'done'; route: string }
  >({ kind: 'redeeming' });

  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', message: 'No claim token in URL.' });
      return;
    }
    let cancelled = false;
    api.redeemClaimToken(token)
      .then(res => {
        if (cancelled) return;
        localStorage.setItem(TOKEN_KEY, res.token);
        qc.invalidateQueries({ queryKey: ['me'] });
        setState({ kind: 'ready', res });
      })
      .catch(err => {
        if (cancelled) return;
        setState({ kind: 'error', message: err.message ?? 'Could not redeem claim link.' });
      });
    return () => { cancelled = true; };
  }, [token, qc]);

  if (state.kind === 'redeeming') return <PageSpinner />;

  if (state.kind === 'error') {
    return (
      <div className="mx-auto max-w-lg px-4 py-12">
        <Card className="border-red-700/40">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 shrink-0 text-red-400" />
            <div>
              <h1 className="font-display text-lg font-black uppercase tracking-wider text-red-200">Link unusable</h1>
              <p className="mt-2 text-sm text-iron-200">{state.message}</p>
              <p className="mt-3 text-xs text-iron-500">
                If this was unexpected, ask the admin who sent you the link to send another one.
              </p>
              <Link to="/" className="mt-4 inline-block font-display text-[11px] font-black uppercase tracking-wider text-forge-300 hover:text-forge-200">
                ← Back to portal
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (state.kind === 'done') {
    navigate(state.route, { replace: true });
    return <PageSpinner />;
  }

  // ready or acting → render the action surface for the token type.
  const { res } = state.kind === 'ready' ? state : { res: undefined as never };
  const type = res.type;

  if (type === 'invite-clan-leader') {
    // clanName is the canonical id — keeps driving the /clan/:name nav below.
    // clanLabel is display-only (displayClanName-formatted); use it for every
    // human-visible render of the clan's name on this screen.
    const clanName = String(res.payload.clanName ?? 'a clan');
    const clanLabel = displayClanName(clanName);
    const inviterDisplayName = String(res.payload.inviterDisplayName ?? 'an admin');
    const accepted = res.inviteAccepted === true;

    if (accepted) {
      const goToClan = () => navigate(`/clan/${encodeURIComponent(clanName)}`, { replace: true });
      return (
        <div className="mx-auto max-w-lg px-4 py-12">
          <Card>
            <h1 className="font-display text-xl font-black uppercase tracking-wider text-emerald-300">
              You're now leading {clanLabel}
            </h1>
            <p className="mt-3 text-sm text-iron-300">
              Welcome, leader. {inviterDisplayName} confirmed you as the new leader of <strong className="text-white">{clanLabel}</strong>.
            </p>
            <p className="mt-2 text-xs text-iron-500">
              Open the clan to schedule matches, manage seats, review applications.
            </p>
            <button
              onClick={goToClan}
              className="ember mt-6 inline-flex items-center gap-2 border border-forge-500/60 bg-forge-600/30 px-4 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-forge-500/40"
            >
              Open {clanLabel}
            </button>
          </Card>
        </div>
      );
    }

    // Token redeemed but the leadership write didn't land (e.g. clan was
    // deleted between mint and redeem). Surface the situation.
    return (
      <div className="mx-auto max-w-lg px-4 py-12">
        <Card className="border-amber-700/40">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 shrink-0 text-amber-400" />
            <div>
              <h1 className="font-display text-lg font-black uppercase tracking-wider text-amber-200">
                Invite couldn't be applied
              </h1>
              <p className="mt-2 text-sm text-iron-200">
                Your invite for <strong className="text-white">{clanLabel}</strong> was valid, but
                the leadership update didn't go through. The clan may have been removed.
              </p>
              <p className="mt-3 text-xs text-iron-500">
                Ask {inviterDisplayName} to send a fresh invite.
              </p>
              <Link to="/" className="mt-4 inline-block font-display text-[11px] font-black uppercase tracking-wider text-forge-300 hover:text-forge-200">
                ← Back to portal
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (type === 'claim-seat') {
    const clanId = String(res.payload.clanId ?? '');
    const gamertag = String(res.payload.gamertag ?? '');
    // Display-only — api.claimSeat below is called with clanId (raw), never
    // this formatted label.
    const clanName = displayClanName(clanId.replace(/^CLAN#/, ''));

    // T08 one-click path: the backend claimed the seat during redemption —
    // clicking the DM button WAS the claim. Straight to the success surface.
    if (res.claimCompleted) {
      return (
        <div className="mx-auto max-w-lg px-4 py-12">
          <Card>
            <h1 className="font-display text-xl font-black uppercase tracking-wider text-emerald-300">
              You're in — welcome to {clanName}
            </h1>
            <p className="mt-3 text-sm text-iron-300">
              The <code className="font-mono text-amber-200">{gamertag}</code> seat is yours.
              Your clan leader has been notified.
            </p>
            <button
              onClick={() => { qc.invalidateQueries({ queryKey: ['me'] }); navigate('/me/org', { replace: true }); }}
              className="ember mt-6 inline-flex items-center gap-2 border border-forge-500/60 bg-forge-600/30 px-4 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-forge-500/40"
            >
              <Check className="h-4 w-4" strokeWidth={2.5} /> Take me in
            </button>
          </Card>
        </div>
      );
    }
    if (res.claimError) {
      return (
        <div className="mx-auto max-w-lg px-4 py-12">
          <Card className="border-amber-700/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 shrink-0 text-amber-400" />
              <div>
                <h1 className="font-display text-lg font-black uppercase tracking-wider text-amber-200">Seat unavailable</h1>
                <p className="mt-2 text-sm text-iron-200">{res.claimError}</p>
                <p className="mt-3 text-xs text-iron-500">Ask the leader who sent the link to check the seat and send a fresh one.</p>
                <Link to="/" className="mt-4 inline-block font-display text-[11px] font-black uppercase tracking-wider text-forge-300 hover:text-forge-200">
                  ← Back to portal
                </Link>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    const handleConfirm = async () => {
      setState({ kind: 'acting' });
      try {
        await api.claimSeat(clanId, gamertag);
        qc.invalidateQueries({ queryKey: ['me'] });
        setState({ kind: 'done', route: '/me/org' });
      } catch (err) {
        setState({ kind: 'error', message: parseApiError(err).message ?? 'Could not claim seat.' });
      }
    };

    const handleReject = () => navigate('/', { replace: true });

    return (
      <div className="mx-auto max-w-lg px-4 py-12">
        <Card>
          <h1 className="font-display text-xl font-black uppercase tracking-wider text-white">Is this you?</h1>
          <p className="mt-3 text-sm text-iron-300">
            You've been reserved a seat in <strong className="text-white">{clanName}</strong> under
            the gamertag <code className="font-mono text-amber-200">{gamertag}</code>.
          </p>
          <p className="mt-2 text-xs text-iron-500">
            Confirming will collapse the ghost seat into your account. You'll appear in the clan as
            a member from now on.
          </p>
          {state.kind === 'acting' && (
            <p className="mt-3 flex items-center gap-2 text-xs text-iron-400">
              <Loader2 className="h-3 w-3 animate-spin" /> Claiming…
            </p>
          )}
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={handleConfirm}
              disabled={state.kind === 'acting'}
              className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-600 px-4 py-2 font-display text-xs font-black uppercase tracking-wider text-white disabled:opacity-50"
            >
              <Check className="h-4 w-4" strokeWidth={2.5} />
              Yes, that's me
            </button>
            <button
              onClick={handleReject}
              disabled={state.kind === 'acting'}
              className="inline-flex items-center gap-2 border border-iron-700 hover:border-iron-500 px-4 py-2 font-display text-xs font-black uppercase tracking-wider text-iron-300"
            >
              <X className="h-4 w-4" strokeWidth={2.5} />
              No, not me
            </button>
          </div>
          <p className="mt-4 text-[10px] uppercase tracking-wider text-iron-500">
            Link uses remaining: {res.remainingUses}/10
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-12">
      <Card>
        <h1 className="font-display text-xl font-black uppercase tracking-wider text-white">Action queued</h1>
        <p className="mt-3 text-sm text-iron-300">
          You've been signed in. The action surface for <code>{type}</code> isn't wired yet — coming soon.
        </p>
        <Link to="/" className="mt-4 inline-block font-display text-[11px] font-black uppercase tracking-wider text-forge-300 hover:text-forge-200">
          → Continue to portal
        </Link>
      </Card>
    </div>
  );
}
